// REF-6853 (REF-6708 Phase C, C3): the reader for a BARE response body (envelope family E4).
//
// Opt-in, per family. Nothing imports it yet; a service method adopts it when its consumer migrates, and
// states its family in its JSDoc (`/** family: E4 bare · errors: default */`). There is no auto-detection
// of the envelope: the caller picks the reader, and a guard is required (a `<T>` cast is not validation).
//
// Every read ends in exactly one of three outcomes, never a success-shaped default:
//
//   handled  `body._handled === true` — the interceptor already ran the UI (401/403/409/503 on a normal
//            route). Checked FIRST, before the guard runs. `status` is `body.responseCode` when it is a
//            valid HTTP status, else absent. The test is `=== true` only, like every existing `_handled`
//            reader: a placeholder with an extra key is still handled. (A server body that itself carries
//            `_handled: true` is indistinguishable from the placeholder; the backend never sends one.)
//   invalid  the guard rejects the body; or the body is empty (`null`, `undefined`, `""`), whatever the
//            guard says; or a 2xx body carries `responseCode >= 400` (an error delivered as data), even
//            when the guard would accept it.
//   ok       the guard accepts the body and none of the above applies: `{ ok: true, value: body }`.
//
// The guard runs exactly once for every body that is not handled. docs/rules/error-handling.md → "Envelope readers".

export type EnvelopeRead<T> = { ok: true; value: T } | { ok: false; reason: "handled"; status?: number } | { ok: false; reason: "invalid" };

type Envelope = { _handled?: unknown; responseCode?: unknown };

const asEnvelope = (body: unknown): Envelope | undefined => (typeof body === "object" && body !== null ? (body as Envelope) : undefined);

const httpStatus = (value: unknown): number | undefined => (typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 599 ? value : undefined);

const isEmpty = (body: unknown) => body === null || body === undefined || body === "";

// An error delivered as data: a resolved (2xx) body whose own `responseCode` says it failed (BE E1c / R9).
const carriesErrorCode = (envelope: Envelope | undefined) => typeof envelope?.responseCode === "number" && envelope.responseCode >= 400;

const invalid = (): EnvelopeRead<never> => ({ ok: false, reason: "invalid" });

export const readBare = <T>(body: unknown, guard: (value: unknown) => value is T): EnvelopeRead<T> => {
  const envelope = asEnvelope(body);

  if (envelope?._handled === true) {
    const status = httpStatus(envelope.responseCode);

    return status === undefined ? { ok: false, reason: "handled" } : { ok: false, reason: "handled", status };
  }

  if (!guard(body)) return invalid();
  if (isEmpty(body) || carriesErrorCode(envelope)) return invalid();

  return { ok: true, value: body };
};
